import { eConnectorPositionMode } from '@kb3d/models/classes/enums';
import { SketchControlPoint, isConnector } from '@kb3d/models/classes/features';
import { ArrayMap } from '@kb3d/models/classes/handlers';
import { MateNode } from '@kb3d/models/classes/mates/mate-node';
import { isSketchControlPoint } from '@kb3d/models/classes/meshes/mesh-utilities';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';

export enum ConstraintType {
    Sphere, // Constrains the target to a point on a shell around a point
    Line, // Constrains the target to a point along a line through a point
    Plane, // Constrains the target to a point on a plane
    Fixed, // Constrains the target to a fixed point
    None, // Does not constrain the target
}

export interface ConstraintDef {
    [ConstraintType.Sphere]: { x: number; y: number; z: number; radius: number };
    [ConstraintType.Line]: { x: number; y: number; z: number; dx: number; dy: number; dz: number };
    [ConstraintType.Plane]: { x: number; y: number; z: number; nx: number; ny: number; nz: number };
    [ConstraintType.Fixed]: void;
    [ConstraintType.None]: void;
    [key: number]: unknown;
}

type ConstraintDefMap = {
    [key in ConstraintType]: {
        type: key;
        properties: ConstraintDef[key];
    };
};
type MateConstraint = ConstraintDefMap[keyof ConstraintDefMap];

type MatedObject = SpaceNode | SketchControlPoint;

interface MateEdge {
    target: MatedObject;
    /** if a mate is not constraining the target node's other  */
    constraint: MateConstraint;
    /** if mate is null means it's a parent/child edge */
    mate?: MateNode;
}

export interface MateSorterResult {
    sorted: Set<MateNode>;
    cycles: MateNode[][];
    overConstrained: MateNode[];
}

export function sortMates(validMates: MateNode[]) {
    const fixed = new Set<SpaceNode>();
    const primes = new Set<SpaceNode>();
    const nodeMap = getNodeMap(validMates);

    //parent/child relationships are also a kind of edge in our graph, so add them
    for (const [node] of nodeMap) {
        if (node instanceof SpaceNode) {
            if (node.fixed) {
                fixed.add(node);
            } else if (node._primeMover) {
                primes.add(node);
            }
            for (const ancestor of node.getAncestors()) {
                if (nodeMap.has(ancestor as MatedObject)) {
                    nodeMap.add(node, {
                        target: ancestor as MatedObject,
                        constraint: { type: ConstraintType.None, properties: undefined },
                    });
                    nodeMap.add(ancestor as MatedObject, {
                        target: node,
                        constraint: { type: ConstraintType.Fixed, properties: undefined },
                    });
                }
            }
        }
    }

    /**
     * * starting with primes, recurse through.  Then move to non-primes, ignoring those already visited.
     */
    const visitedNodes = new Set<MatedObject>();
    const visitedMates = new Set<MateNode>();
    const nodePath = new Set<MatedObject>();
    const matePath = new Set<MateNode>();
    const sorted = new Set<MateNode>();
    const cycles = new Array<MateNode[]>();
    const overConstrained = new Array<MateNode>();

    for (const n of [...fixed, ...primes, ...nodeMap.keys()]) {
        recurseMateChain(nodeMap, visitedNodes, visitedMates, nodePath, matePath, sorted, cycles, overConstrained, n);
    }

    //remove overconstrained mates from sorted so they don't get processed
    for (const m of overConstrained) {
        sorted.delete(m);
    }

    return {
        sorted,
        cycles,
        overConstrained,
    } as MateSorterResult;
}

/**
 * Gets the nodes that are connected to the given through a chain of mates
 */
export function getNodeChain(node: MatedObject, validMates: MateNode[]) {
    const nodeMap = getNodeMap(validMates);
    const visitedNodes = new Set<MatedObject>();
    const visitedMates = new Set<MateNode>();
    const nodePath = new Set<MatedObject>();
    const matePath = new Set<MateNode>();
    const sorted = new Set<MateNode>();
    const cycles = new Array<MateNode[]>();
    const overConstrained = new Array<MateNode>();

    recurseMateChain(nodeMap, visitedNodes, visitedMates, nodePath, matePath, sorted, cycles, overConstrained, node);

    visitedNodes.delete(node);

    return visitedNodes;
}

export function getNodeMap(validMates: MateNode[]) {
    const nodeMap = new ArrayMap<MatedObject, MateEdge>();
    for (const mate of validMates) {
        const c1 = mate.getC1();
        const c2 = mate.getC2();
        let c1Parent: SpaceNode | SketchControlPoint | undefined = c1?.getSpaceParent();
        let c2Parent: SpaceNode | SketchControlPoint | undefined = c2?.getSpaceParent();
        if (c1 && isSketchControlPoint(c1)) {
            c1Parent = c1;
        }

        if (c2 && isSketchControlPoint(c2)) {
            c2Parent = c2;
        }

        if (c1 && c2 && c1Parent && c2Parent) {
            const constraint: MateConstraint = { type: ConstraintType.Fixed, properties: undefined };

            // TODO [RO] - Later on we can add more constraint types here based on mate type (CPQ-3881)
            //if (isConstrainingConnector(c1) && isConstrainingConnector(c2)) {
            // constraint = { type: ConstraintType.Fixed, properties: undefined };
            // } else {
            //     constraint = { type: ConstraintType.None, properties: undefined };
            // }

            nodeMap.add(c1Parent, { target: c2Parent, mate, constraint });
            nodeMap.add(c2Parent, { target: c1Parent, mate, constraint });
        }
    }
    return nodeMap;
}

function recurseMateChain(
    nodeMap: ArrayMap<MatedObject, MateEdge>,
    visitedNodes: Set<MatedObject>,
    visitedMates: Set<MateNode>,
    nodePath: Set<MatedObject>,
    matePath: Set<MateNode>,
    sorted: Set<MateNode>,
    cycles: MateNode[][],
    overConstrained: MateNode[],
    node: MatedObject,
    mate?: MateNode,
    sourceEdge?: MateEdge,
    isOverconstrained = false
) {
    if (!sourceEdge || sourceEdge.constraint.type === ConstraintType.Fixed) {
        if (nodePath.has(node)) {
            const cycle = [...matePath];
            if (mate) {
                cycle.push(mate);
            }
            cycles.push(cycle);
        }

        if (visitedNodes.has(node)) {
            return;
        }
    }
    visitedNodes.add(node);
    nodePath.add(node);

    if (mate) {
        matePath.add(mate);
        visitedMates.add(mate);

        sorted.add(mate);
        const c1 = mate.getC1()!;
        const c2 = mate.getC2()!;
        /** Move along sketch connectors should always be the connector 1, because they can't be moved (they are read only) */
        if (c2.positionMode === eConnectorPositionMode.moveAlongSketch) {
            mate._swap = true;
        }
        /** Control points are always follower meshes */
        else if (isConnector(c1) && isSketchControlPoint(c2)) {
            mate._swap = false;
        } else if (isConnector(c2) && isSketchControlPoint(c1)) {
            mate._swap = true;
        } else {
            /** mate needs to have c1/c2 swapped if we got here in the opposite direction */
            mate._swap = c1._parent! === node;
        }
    }

    // If the source edge is fixed, we recurse through the node because all the node's connectors will be shifting.
    // If the source edge is not fixed, we stop here because only the mated connector will move
    // [RO] TODO eventually we will have to rethink this logic to support more constraint types (CPQ-3881)
    if (!sourceEdge || sourceEdge.constraint.type === ConstraintType.Fixed) {
        for (const edge of nodeMap.get(node)) {
            if (!edge.mate || !visitedMates.has(edge.mate)) {
                if (
                    edge.target instanceof SpaceNode &&
                    edge.target.fixed &&
                    edge.constraint.type !== ConstraintType.None
                ) {
                    isOverconstrained = true;
                }
                recurseMateChain(
                    nodeMap,
                    visitedNodes,
                    visitedMates,
                    nodePath,
                    matePath,
                    sorted,
                    cycles,
                    overConstrained,
                    edge.target,
                    edge.mate,
                    edge,
                    isOverconstrained
                );
            }
        }
    }

    nodePath.delete(node);
    if (mate) {
        matePath.delete(mate);
        if (isOverconstrained) {
            overConstrained.push(mate);
        }
        visitedMates.delete(mate);
    }
}
